import * as THREE from 'three';
import { EV } from '../core/EventBus.js';

const glowing = color => {
  const m = new THREE.MeshBasicMaterial({ color, toneMapped: false });
  m.userData.owned = true;
  return m;
};
const box = (size, material, position) => {
  const m = new THREE.Mesh(new THREE.BoxGeometry(...size), material);
  m.position.copy(position);
  return m;
};

export function registerBeamPrefabs(prefabs) {
  prefabs.register('emitter', (def, ctx) => {
    const position = new THREE.Vector3().fromArray(def.p);
    const direction = new THREE.Vector3().fromArray(def.dir ?? [0, 0, -1]).normalize();
    const material = glowing(0xffb020);
    const source = new THREE.Mesh(new THREE.SphereGeometry(0.42, 12, 8), material);
    source.position.copy(position);
    const housing = box([1, 1, 1], ctx.materials.get('trim'), position.clone().addScaledVector(direction, -0.6));
    const beams = [0, 1].map(() => box([0.13, 0.13, 1], material, position));
    const lines = [new THREE.Line3(), new THREE.Line3()];
    const lastShock = new WeakMap();
    const point = new THREE.Vector3();
    const triggers = lines.map(line => ({
      center: position.clone(), half: new THREE.Vector3(), enabled: false, inside: false, anyBody: true,
      onStay(dt, c) {
        const ctrl = c.player.controller, now = c.level.elapsed;
        if (now - (lastShock.get(ctrl) ?? -10) < 0.8) return;
        const hit = (c.sweepPoints ?? [ctrl.position]).some(p => {
          line.closestPointToPoint(p, true, point);
          return point.distanceToSquared(p) < (ctrl.radius + 0.22) ** 2;
        });
        if (hit && (def.shock ?? 2) > 0) {
          ctrl.applyShock(def.shock ?? 2); lastShock.set(ctrl, now);
          c.bus.emit(EV.SHEAR, { position: point.toArray(), stripped: 0 });
          c.bus.emit(EV.TOAST, { text: 'BEAM SHOCK\nSPEED AND JUMP CUT', warn: true });
        }
      },
    }));
    return {
      role: 'emitter', position, direction, range: def.range ?? 120,
      objects: [source, housing, ...beams], triggers,
      // A no-op keeps this stateful prefab in LevelInstance.entities.
      update() {},
      showSegments(segments) {
        for (let i = 0; i < 2; i++) {
          const s = segments[i], m = beams[i], t = triggers[i];
          m.visible = t.enabled = !!s;
          if (!s) continue;
          lines[i].set(s.start, s.end);
          m.position.copy(s.start).add(s.end).multiplyScalar(0.5);
          m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), s.end.clone().sub(s.start).normalize());
          m.scale.z = Math.max(0.001, s.start.distanceTo(s.end));
          t.center.copy(m.position);
          t.half.subVectors(s.end, s.start).multiplyScalar(0.5);
          t.half.set(Math.abs(t.half.x) + 0.8, Math.abs(t.half.y) + 0.8, Math.abs(t.half.z) + 0.8);
        }
      },
    };
  });

  prefabs.register('receptacle', (def) => {
    const position = new THREE.Vector3().fromArray(def.p), radius = def.r ?? 0.9;
    const material = glowing(0x784614);
    const sphere = new THREE.Mesh(new THREE.SphereGeometry(radius, 16, 12), material);
    sphere.position.copy(position);
    const ring = new THREE.Mesh(new THREE.TorusGeometry(radius * 1.3, 0.12, 8, 24), glowing(0xffb020));
    ring.position.copy(position);
    const entity = {
      role: 'receptacle', id: def.id, position, radius, powered: false,
      objects: [sphere, ring], update() {},
      showPower() { material.color.setHex(entity.powered ? 0x9dff3c : 0x784614); },
    };
    return entity;
  });

  prefabs.register('gate', (def, ctx) => {
    const w = def.w ?? 6, h = def.h ?? 8, yaw = def.yaw ?? 0;
    const position = new THREE.Vector3().fromArray(def.p).add(new THREE.Vector3(0, h / 2, 0));
    const material = glowing(0x843c12);
    const mesh = box([w, h, 0.5], ctx.materials.get('trim'), position);
    mesh.rotation.y = THREE.MathUtils.degToRad(yaw);
    const bars = box([w * 0.9, 0.18, 0.56], material, position);
    bars.rotation.y = mesh.rotation.y;
    const collider = ctx.world.addBox({position:position.toArray(),halfExtents:[w/2,h/2,0.25],rotation:[0,yaw,0],portalable:false,tags:['beam-gate']});
    const inverse = mesh.quaternion.clone().invert(), p = new THREE.Vector3();
    let remaining = 0;
    const entity = {
      role: 'gate', link: def.link, powered: false, open: false,
      objects: [mesh, bars], colliders: [collider], update() {},
      setPower(powered, dt, gameCtx) {
        entity.powered = powered;
        remaining = powered ? (def.grace ?? 2) : Math.max(0, remaining - dt);
        // Never close through an occupied doorway. Portal body samples
        // include both player and rival, and are independent of camera POV.
        let occupied = false;
        for (const body of gameCtx.portals?.bodies.values() ?? []) {
          body.getSamplePoints(body.points);
          for (const sample of body.points) {
            p.copy(sample).sub(position).applyQuaternion(inverse);
            if (Math.abs(p.x) < w/2 + 0.5 && Math.abs(p.y) < h/2 + 0.5 && Math.abs(p.z) < 0.8) occupied = true;
          }
        }
        entity.open = powered || remaining > 0 || (entity.open && occupied);
        collider.enabled = !entity.open;
        mesh.visible = !entity.open;
        material.color.setHex(entity.open ? 0x9dff3c : 0xff8a1f);
        bars.position.y = entity.open ? def.p[1] + h - 0.2 : position.y;
      },
    };
    return entity;
  });
}
